import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Button,
  useTheme
} from '@mui/material';
import { activityApi } from '../../services/api';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

const DistributorActivity = () => {
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  console.log("theme = ",theme)

  // Sorting state
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('last_viewed_on');

  useEffect(() => {
    const fetchDistributorActivity = async () => {
      try {
        setLoading(true);
        const response = await activityApi.getDistributorActivity();
        setDistributors(response.data.distributors);
        setError(null);
      } catch (err) {
        console.error('Error fetching distributor activity:', err);
        setError('Failed to load distributor activity data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDistributorActivity();
  }, []);

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort function
  const sortedDistributors = React.useMemo(() => {
    if (!distributors.length) return [];

    return [...distributors].sort((a, b) => {
      // Handle null values
      if (!a[orderBy] && !b[orderBy]) return 0;
      if (!a[orderBy]) return 1;
      if (!b[orderBy]) return -1;

      // For date fields
      if (orderBy === 'last_viewed_on' || orderBy === 'last_uploaded_on') {
        const dateA = a[orderBy] ? new Date(a[orderBy]) : new Date(0);
        const dateB = b[orderBy] ? new Date(b[orderBy]) : new Date(0);
        return order === 'asc'
          ? dateA - dateB
          : dateB - dateA;
      }

      // For text fields
      const result = a[orderBy].localeCompare(b[orderBy]);
      return order === 'asc' ? result : -result;
    });
  }, [distributors, order, orderBy]);

  // Excel download handler
  const handleDownloadExcel = () => {
    // Prepare data for Excel
    const excelData = sortedDistributors.map(distributor => ({
      'Distributor Name': distributor.name,
      'City': distributor.city || 'N/A',
      'Association': distributor.association || 'N/A',
      'Last Viewed On': distributor.last_viewed_on || 'Never',
      'Last Uploaded On': distributor.last_uploaded_on || 'Never'
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Distributor Activity");

    // Auto-size columns
    const colWidths = [
      { wch: 25 }, // Distributor Name
      { wch: 15 }, // City
      { wch: 25 }, // Association
      { wch: 15 }, // Last Viewed On
      { wch: 15 }  // Last Uploaded On
    ];
    worksheet['!cols'] = colWidths;

    // Generate filename with date
    const now = new Date();
    const fileName = `Distributor_Activity_${now.toISOString().split('T')[0]}.xlsx`;

    // Download
    XLSX.writeFile(workbook, fileName);
  };

  // Helper function to render date display - completely abandoning Chip component
  const renderDateChip = (date, icon) => {
    // Base styles for consistent display in both light/dark modes
    const baseContainerStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '16px',
      border: '1px solid #666666'
    };
    
    // Styles for "Never" display
    if (!date) {
      return (
        <Box sx={{
          ...baseContainerStyle,
          backgroundColor: '#333333',
          border: '1px solid #666666'
        }}>
          {React.cloneElement(icon, { 
            style: { 
              color: theme.palette.primary.main,
              marginRight: '5px',
              fontSize: '1rem'
            } 
          })}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold',
              color: '#ffffff'
            }}
          >
            Never
          </Typography>
        </Box>
      );
    }

    // Calculate if date is recent
    const dateObj = new Date(date);
    const today = new Date();
    const daysDiff = Math.floor((today - dateObj) / (1000 * 60 * 60 * 24));
    
    // For recent dates (within 7 days)
    if (daysDiff <= 7) {
      return (
        <Box sx={{
          ...baseContainerStyle,
          backgroundColor: theme.palette.mode === 'dark'
            ? '#1a4073' // Darker blue for dark mode
            : theme.palette.primary.main,
          border: 'none'
        }}>
          {React.cloneElement(icon, {
            style: {
              color: '#ffffff',
              marginRight: '5px',
              fontSize: '1rem'
            }
          })}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: '#ffffff'
            }}
          >
            {date}
          </Typography>
        </Box>
      );
    } 
    // For older dates
    else {
      return (
        <Box sx={{
          ...baseContainerStyle,
          backgroundColor: '#333333',
          border: '1px solid #666666'
        }}>
          {React.cloneElement(icon, { 
            style: { 
              color: theme.palette.primary.main,
              marginRight: '5px',
              fontSize: '1rem'
            } 
          })}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold',
              color: '#ffffff'
            }}
          >
            {date}
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 2 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          bgcolor: 'background.paper',
          mb: 4,
          border: theme => `1px solid ${theme.palette.divider}`,
          boxShadow: theme =>
            theme.palette.mode === 'dark'
              ? '0 4px 20px 0 rgba(0,0,0,0.4)'
              : '0 4px 20px 0 rgba(0,0,0,0.05)',
          width: '100%'
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                Distributor Activity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track when distributors last viewed or uploaded data to the platform.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExcel}
              disabled={loading || distributors.length === 0}
              sx={{ height: 40 }}
            >
              Export to Excel
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, width: '100%' }}>
              <Table sx={{ minWidth: 750 }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2">Distributor</Typography>
                        </Box>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="15%">
                      <TableSortLabel
                        active={orderBy === 'city'}
                        direction={orderBy === 'city' ? order : 'asc'}
                        onClick={() => handleRequestSort('city')}
                      >
                        <Typography variant="subtitle2">City</Typography>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="25%">
                      <TableSortLabel
                        active={orderBy === 'last_viewed_on'}
                        direction={orderBy === 'last_viewed_on' ? order : 'asc'}
                        onClick={() => handleRequestSort('last_viewed_on')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2">Last Viewed On</Typography>
                        </Box>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="30%">
                      <TableSortLabel
                        active={orderBy === 'last_uploaded_on'}
                        direction={orderBy === 'last_uploaded_on' ? order : 'asc'}
                        onClick={() => handleRequestSort('last_uploaded_on')}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CloudUploadIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="subtitle2">Last Uploaded On</Typography>
                        </Box>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDistributors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" sx={{ py: 4 }}>
                          No distributor activity data available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedDistributors.map((distributor) => (
                      <TableRow
                        key={distributor.id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {distributor.name}
                          </Typography>
                          {distributor.association && (
                            <Typography variant="caption" color="text.secondary">
                              {distributor.association}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {distributor.city || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {renderDateChip(distributor.last_viewed_on, <AccessTimeIcon fontSize="small" />)}
                        </TableCell>
                        <TableCell>
                          {renderDateChip(distributor.last_uploaded_on, <CloudUploadIcon fontSize="small" />)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DistributorActivity;